/**
 * useVerifyNextFunnel — verify-next walk over a live `toVerify` list.
 *
 * For the file-drop funnel the list is already filtered to this drop (E3).
 * Done fires when that filtered list hits zero, even if other unverified
 * items remain on the Bible. Skip is session-only. Stop ends the walk but
 * does not throw work away (E3/E8).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { AttentionRow } from '@/components/brand-bible/NeedsAttentionQueue';

export type FunnelPhase = 'idle' | 'precommit' | 'postcommit' | 'done';

export interface VerifyNextFunnelState {
  phase: FunnelPhase;
  originalCount: number | null;
  verifiedCount: number;
  focused: AttentionRow | null;
  upcoming: AttentionRow | null;
  start: () => void;
  verifyNext: () => void;
  skip: () => void;
  stop: () => void;
  lastCommitted: AttentionRow | null;
  commitFocused: () => Promise<void>;
  rejectFocused: () => Promise<void>;
}

const rowKeyOf = (row: AttentionRow) => `${row.table}:${row.entry.id}`;

function isGoldenExample(row: AttentionRow): boolean {
  return row.table === 'chunks' && (row.entry as { kind?: string }).kind === 'golden_example';
}

export function useVerifyNextFunnel(toVerify: AttentionRow[]): VerifyNextFunnelState {
  const verifyEntry = useBrandBibleWizardStore((s) => s.verifyEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const [phaseState, setPhase] = useState<FunnelPhase>('idle');
  const [originalCount, setOriginalCount] = useState<number | null>(null);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [lastCommitted, setLastCommitted] = useState<AttentionRow | null>(null);

  const byKey = useMemo(() => {
    const map = new Map<string, AttentionRow>();
    toVerify.forEach((row) => map.set(rowKeyOf(row), row));
    return map;
  }, [toVerify]);

  const unskippedQueue = useMemo(
    () => toVerify.filter((row) => !skipped.has(rowKeyOf(row))),
    [toVerify, skipped],
  );

  const liveCount = toVerify.length;
  const phase: FunnelPhase =
    phaseState !== 'idle' && phaseState !== 'done' && liveCount === 0
      ? 'done'
      : phaseState === 'precommit' && focusedKey && !byKey.has(focusedKey)
        ? 'postcommit'
        : phaseState;
  const effectiveQueue =
    phase === 'postcommit' && liveCount > 0 && unskippedQueue.length === 0
      ? toVerify
      : unskippedQueue;

  const start = useCallback(() => {
    if (toVerify.length === 0) return;
    setOriginalCount(toVerify.length);
    setSkipped(new Set());
    setFocusedKey(rowKeyOf(toVerify[0]));
    setPhase('precommit');
  }, [toVerify]);

  const verifyNext = useCallback(() => {
    const next = effectiveQueue[0];
    if (!next) return;
    setFocusedKey(rowKeyOf(next));
    setPhase('precommit');
  }, [effectiveQueue]);

  const skip = useCallback(() => {
    const current = effectiveQueue[0];
    if (!current) return;
    setSkipped((prev) => new Set(prev).add(rowKeyOf(current)));
  }, [effectiveQueue]);

  const stop = useCallback(() => {
    setPhase('idle');
    setOriginalCount(null);
    setFocusedKey(null);
    setSkipped(new Set());
    setLastCommitted(null);
  }, []);

  useEffect(() => {
    if (phase !== 'precommit' && phase !== 'postcommit') return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, stop]);

  const focused = phase === 'precommit' && focusedKey ? byKey.get(focusedKey) ?? null : null;
  const focusedIndex = focusedKey ? effectiveQueue.findIndex((row) => rowKeyOf(row) === focusedKey) : -1;
  const upcoming =
    phase === 'precommit' ? effectiveQueue[focusedIndex + 1] ?? null : effectiveQueue[0] ?? null;

  const commitFocused = useCallback(async () => {
    if (!focused) return;
    const committed = focused;
    await verifyEntry(focused.table, focused.entry.id, undefined, bbTenant);
    setLastCommitted(committed);
    setPhase('postcommit');
  }, [focused, verifyEntry]);

  const rejectFocused = useCallback(async () => {
    if (!focused) return;
    const committed = focused;
    // E4: a rejected golden stays as a negative example — do not silent-delete.
    if (isGoldenExample(focused)) {
      await upsertEntry('chunks', { kind: 'rejected_example' }, focused.entry.id, bbTenant);
      await verifyEntry(focused.table, focused.entry.id, undefined, bbTenant);
    } else {
      await discardEntry(focused.table, focused.entry.id, bbTenant);
    }
    setLastCommitted(committed);
    setPhase('postcommit');
  }, [focused, upsertEntry, verifyEntry, discardEntry]);

  return {
    phase,
    originalCount,
    verifiedCount: originalCount !== null ? Math.max(0, originalCount - liveCount) : 0,
    focused,
    upcoming,
    start,
    verifyNext,
    skip,
    stop,
    lastCommitted,
    commitFocused,
    rejectFocused,
  };
}
